// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Triage Question Configuration
// Build, preview, and manage intake triage questions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Switch, NumberInput,
  Card, Radio, Slider,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconArrowUp, IconArrowDown, IconEye,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import type { TriageQuestion } from '../../types/shelter';

const QUESTION_TYPE_OPTIONS = [
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'text', label: 'Free Text' },
  { value: 'scale', label: 'Scale (1-10)' },
];

const CATEGORY_OPTIONS = [
  { value: 'medical', label: 'Medical' },
  { value: 'housing', label: 'Housing' },
  { value: 'safety', label: 'Safety' },
  { value: 'general', label: 'General' },
];

const CATEGORY_COLORS: Record<string, string> = {
  medical: 'var(--color-error)', housing: 'var(--color-primary-700)', safety: '#F86A0B', general: '#939598',
};

const DEFAULT_QUESTIONS: Partial<TriageQuestion>[] = [
  { question: 'Do you have any immediate medical needs?', questionType: 'yes_no', category: 'medical', sortOrder: 1, isActive: true, flagIfAnswer: 'Yes' },
  { question: 'Are you currently taking any medications?', questionType: 'yes_no', category: 'medical', sortOrder: 2, isActive: true, flagIfAnswer: 'Yes' },
  { question: 'Do you have any mobility or accessibility needs?', questionType: 'yes_no', category: 'medical', sortOrder: 3, isActive: true, flagIfAnswer: 'Yes' },
  { question: 'Do you have a safe place to return to after the shelter closes?', questionType: 'yes_no', category: 'housing', sortOrder: 4, isActive: true, flagIfAnswer: 'No' },
  { question: 'How would you describe the condition of your home?', questionType: 'multiple_choice', options: '["Undamaged","Minor damage","Major damage","Destroyed","Don\'t know"]', category: 'housing', sortOrder: 5, isActive: true, flagIfAnswer: 'Destroyed' },
  { question: 'Do you feel safe in your current situation?', questionType: 'yes_no', category: 'safety', sortOrder: 6, isActive: true, flagIfAnswer: 'No' },
  { question: 'Rate your current stress level (1=low, 10=high)', questionType: 'scale', category: 'general', sortOrder: 7, isActive: true, flagIfAnswer: '8' },
  { question: 'Do you have children or dependents with you?', questionType: 'yes_no', category: 'general', sortOrder: 8, isActive: true, flagIfAnswer: '' },
  { question: 'Do you need help contacting family members?', questionType: 'yes_no', category: 'general', sortOrder: 9, isActive: true, flagIfAnswer: 'Yes' },
  { question: 'Additional notes or concerns:', questionType: 'text', category: 'general', sortOrder: 10, isActive: true, flagIfAnswer: '' },
];

const EMPTY: Partial<TriageQuestion> = {
  question: '', questionType: 'yes_no', options: '[]', category: 'general',
  sortOrder: 0, isActive: true, flagIfAnswer: '',
};

export default function TriageSetup() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    triageQuestions,
    loadingTriageQ,
    loadTriageQuestions,
    createTriageQuestion, updateTriageQuestion, deleteTriageQuestion,
  } = useShelterStore();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TriageQuestion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TriageQuestion | null>(null);
  const [form, setForm] = useState<Partial<TriageQuestion>>({ ...EMPTY });
  const [optionsText, setOptionsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => { loadTriageQuestions(); }, [loadTriageQuestions]);

  // Store already server-side filters by active incident; use directly
  const iQuestions = incidentId ? triageQuestions : [];

  // Seed default questions if empty on first load
  const seedDefaults = useCallback(async () => {
    if (seeded || loadingTriageQ || iQuestions.length > 0) return;
    setSeeded(true);
    // Don't seed yet - let user do it manually
  }, [seeded, loadingTriageQ, iQuestions.length]);

  useEffect(() => { seedDefaults(); }, [seedDefaults]);

  const handleSeedDefaults = async () => {
    setSaving(true);
    try {
      for (const q of DEFAULT_QUESTIONS) {
        await createTriageQuestion({
          ...q,
          incidentId,
          options: q.options ?? '[]',
        });
      }
      notifications.show({ message: `${DEFAULT_QUESTIONS.length} default questions created`, color: 'var(--color-success)' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Seed failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    let list = iQuestions;
    if (categoryFilter && categoryFilter !== 'all') list = list.filter(q => q.category === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(tq => tq.question.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [iQuestions, categoryFilter, search]);

  const activeQuestions = useMemo(() =>
    [...iQuestions].filter(q => q.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [iQuestions],
  );

  const setField = (key: keyof TriageQuestion, value: unknown) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    const maxSort = iQuestions.reduce((max, q) => Math.max(max, q.sortOrder), 0);
    setForm({ ...EMPTY, sortOrder: maxSort + 1 });
    setOptionsText('');
    setModalOpen(true);
  };

  const openEdit = (q: TriageQuestion) => {
    setEditTarget(q);
    setForm({ ...q });
    // Parse options for the text editor
    try {
      const parsed = JSON.parse(q.options || '[]');
      setOptionsText(Array.isArray(parsed) ? parsed.join('\n') : '');
    } catch {
      setOptionsText('');
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.question?.trim()) {
      notifications.show({ message: 'Question text is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      // Build options JSON from text area
      let options = '[]';
      if (form.questionType === 'multiple_choice' && optionsText.trim()) {
        const items = optionsText.split('\n').map(s => s.trim()).filter(Boolean);
        options = JSON.stringify(items);
      }

      const payload: Partial<TriageQuestion> = {
        ...form,
        incidentId,
        options,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive ?? true,
      };

      if (editTarget) {
        await updateTriageQuestion(editTarget.Rid, payload);
        notifications.show({ message: 'Question updated', color: 'var(--color-success)' });
      } else {
        await createTriageQuestion(payload);
        notifications.show({ message: 'Question created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteTriageQuestion(deleteTarget.Rid);
      notifications.show({ message: 'Question deleted', color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleReorder = async (q: TriageQuestion, direction: 'up' | 'down') => {
    const sorted = [...filtered];
    const idx = sorted.findIndex(sq => sq.Rid === q.Rid);
    if (direction === 'up' && idx <= 0) return;
    if (direction === 'down' && idx >= sorted.length - 1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const swapWith = sorted[swapIdx];

    try {
      await updateTriageQuestion(q.Rid, { sortOrder: swapWith.sortOrder });
      await updateTriageQuestion(swapWith.Rid, { sortOrder: q.sortOrder });
      notifications.show({ message: 'Order updated', color: 'var(--color-success)' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Reorder failed', color: 'var(--color-error)' });
    }
  };

  const handleToggleActive = async (q: TriageQuestion) => {
    try {
      await updateTriageQuestion(q.Rid, { isActive: !q.isActive });
      notifications.show({ message: q.isActive ? 'Question disabled' : 'Question enabled', color: 'var(--color-primary-700)' });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Toggle failed', color: 'var(--color-error)' });
    }
  };

  // ── Preview renderer ─────────────────────────────────────────
  const renderPreview = (q: TriageQuestion) => {
    let opts: string[] = [];
    try { opts = JSON.parse(q.options || '[]'); } catch { /* ignore */ }

    switch (q.questionType) {
      case 'yes_no':
        return (
          <Radio.Group>
            <Group mt="xs"><Radio value="Yes" label="Yes" /><Radio value="No" label="No" /></Group>
          </Radio.Group>
        );
      case 'multiple_choice':
        return (
          <Radio.Group>
            <Stack mt="xs" gap="xs">
              {opts.map((opt, i) => <Radio key={i} value={opt} label={opt} />)}
            </Stack>
          </Radio.Group>
        );
      case 'text':
        return <Textarea mt="xs" placeholder="Enter response..." disabled />;
      case 'scale':
        return <Slider mt="xs" min={1} max={10} step={1} marks={[{ value: 1, label: '1' }, { value: 5, label: '5' }, { value: 10, label: '10' }]} disabled />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Triage Questions</Title>
        <Group gap="xs">
          {iQuestions.length === 0 && (
            <Button size="xs" variant="light" color="var(--data-viz-650)" loading={saving}
              onClick={handleSeedDefaults}>Seed Defaults</Button>
          )}
          <Button size="xs" variant="light" leftSection={<IconEye size={14} />}
            onClick={() => setPreviewOpen(true)} disabled={activeQuestions.length === 0}>Preview</Button>
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingTriageQ} onClick={() => loadTriageQuestions()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Question</Button>
        </Group>
      </Group>

      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput placeholder="Search questions..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
          value={categoryFilter ?? 'all'} onChange={v => setCategoryFilter(v)} size="xs" w={160} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {filtered.length} question{filtered.length !== 1 ? 's' : ''} ({activeQuestions.length} active)
      </Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'center', width: 50 }}>Order</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Question</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '8px 16px', textAlign: 'center' }}>Active</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Flag Answer</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingTriageQ ? 'Loading...' : 'No triage questions configured'}
                </td></tr>
              )}
              {filtered.map((q, idx) => (
                <tr key={q.id}
                  style={{
                    borderBottom: '1px solid #e2e8f0',
                    opacity: q.isActive ? 1 : 0.5,
                  }}>
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <Group gap={2} justify="center">
                      <ActionIcon size="xs" variant="subtle"
                        onClick={() => handleReorder(q, 'up')} disabled={idx === 0}>
                        <IconArrowUp size={12} />
                      </ActionIcon>
                      <Text size="sm" fw={600}>{q.sortOrder}</Text>
                      <ActionIcon size="xs" variant="subtle"
                        onClick={() => handleReorder(q, 'down')} disabled={idx === filtered.length - 1}>
                        <IconArrowDown size={12} />
                      </ActionIcon>
                    </Group>
                  </td>
                  <td style={{ padding: '8px 16px', fontWeight: 500, maxWidth: 350 }}>
                    <Text size="sm" lineClamp={2}>{q.question}</Text>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Badge size="sm" variant="light">{q.questionType.replace(/_/g, ' ')}</Badge>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Badge size="sm" color={CATEGORY_COLORS[q.category] ?? '#939598'}>
                      {q.category}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <Switch size="xs" checked={q.isActive}
                      onChange={() => handleToggleActive(q)} />
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    {q.flagIfAnswer ? (
                      <Badge size="xs" color="var(--color-error)" variant="light">{q.flagIfAnswer}</Badge>
                    ) : (
                      <Text size="xs" c="dimmed">-</Text>
                    )}
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(q)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(q)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Question' : 'Add Question'} centered size="lg">
        <Stack>
          <Textarea label="Question Text" required value={form.question ?? ''}
            onChange={e => setField('question', e.target.value)}
            autosize minRows={2} placeholder="Enter the triage question..." />

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Question Type" data={QUESTION_TYPE_OPTIONS}
              value={form.questionType ?? 'yes_no'}
              onChange={v => setField('questionType', v ?? 'yes_no')} />
            <Select label="Category" data={CATEGORY_OPTIONS}
              value={form.category ?? 'general'}
              onChange={v => setField('category', v ?? 'general')} />
            <NumberInput label="Sort Order" min={0}
              value={form.sortOrder ?? 0}
              onChange={v => setField('sortOrder', v)} />
          </SimpleGrid>

          {form.questionType === 'multiple_choice' && (
            <Textarea label="Options (one per line)" value={optionsText}
              onChange={e => setOptionsText(e.target.value)}
              autosize minRows={3} placeholder={'Option 1\nOption 2\nOption 3'} />
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Switch label="Active" checked={form.isActive ?? true}
              onChange={e => setField('isActive', e.currentTarget.checked)} mt="md" />
            <TextInput label="Flag If Answer" value={form.flagIfAnswer ?? ''}
              onChange={e => setField('flagIfAnswer', e.target.value)}
              placeholder="e.g. Yes, No, Destroyed, 8" />
          </SimpleGrid>

          {/* Inline preview */}
          {form.question && (
            <Card withBorder p="sm" bg="gray.0">
              <Text size="xs" fw={700} c="dimmed" mb="xs">Preview</Text>
              <Text size="sm" fw={500}>{form.question}</Text>
              {renderPreview({
                ...EMPTY,
                ...form,
                options: form.questionType === 'multiple_choice' && optionsText.trim()
                  ? JSON.stringify(optionsText.split('\n').map(s => s.trim()).filter(Boolean))
                  : form.options ?? '[]',
              } as TriageQuestion)}
            </Card>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Question'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Full Preview Modal */}
      <Modal opened={previewOpen} onClose={() => setPreviewOpen(false)}
        title="Triage Questionnaire Preview" centered size="lg">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This is how the triage questionnaire appears to intake staff.
          </Text>
          {activeQuestions.map((q, idx) => (
            <Card key={q.id} withBorder p="sm">
              <Group gap="xs" mb="xs">
                <Badge size="xs" variant="light">{idx + 1}</Badge>
                <Badge size="xs" color={CATEGORY_COLORS[q.category] ?? '#939598'}>{q.category}</Badge>
                {q.flagIfAnswer && (
                  <Badge size="xs" color="var(--color-error)" variant="outline">Flags: {q.flagIfAnswer}</Badge>
                )}
              </Group>
              <Text size="sm" fw={500} mb="xs">{q.question}</Text>
              {renderPreview(q)}
            </Card>
          ))}
          {activeQuestions.length === 0 && (
            <Text c="dimmed" ta="center">No active questions to preview</Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPreviewOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Question" centered size="sm">
        <Stack>
          <Text size="sm">Delete this question? This cannot be undone.</Text>
          <Text size="sm" fw={500} c="dimmed" lineClamp={2}>"{deleteTarget?.question}"</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
